import React from 'react';
import _ from 'lodash';
import {
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  ImageField,
  DateField,
  BooleanField,
  CardActions,
  ListButton,
  ReferenceManyField,
  ArrayField,
  Datagrid,
  UrlField,
  NumberField,
  FunctionField,
  Labeled,
} from 'react-admin';

import {
  getAdminLevel,
  ADMIN_LEVELS,
} from '../../authProvider';
import StyledRichTextField from '../StyledRichTextField';
import EditButton from './EditButton';
import ReductionResultsButton from './ReductionResultsButton';

import { offeringCategory, externalOfferSelection, subscriptionAgreement, securityDeliveryInformationOptionTypes } from '../../helpers/mappings';

const Actions = ({
  basePath,
  data,
  path,
  resource,
}) => {
  const adminLevel = getAdminLevel();

  return (
    <CardActions>
      { adminLevel && adminLevel !== ADMIN_LEVELS.READ_ONLY_ADMIN ?
        <EditButton record={data} basePath={basePath} path={path} /> : undefined
      }
      <ReductionResultsButton disabled={!data || data.overSubscriptionValue <= 0} resource={resource} record={data} />
      <ListButton basePath={basePath} />
    </CardActions>
  );
};

const CloseDateOrDatesField = ({ record }) => {
  if (record && record.recurring) {
    return (
      <ArrayField record={record} source="closeDates">
        <Datagrid>
          <DateField source="date" />
        </Datagrid>
      </ArrayField>
    );
  }
  return (
    <TextField source="closeDate" record={record} />
  );
};

CloseDateOrDatesField.defaultProps = { addLabel: true };

const ImagePreview = ({ record }) => (
  <img src={record.type === 'vimeo' ? record.previewImageUrl : record.url} alt="" width="300" style={{ padding: '10px 0px' }} />
);

const StandardOfferPricePerShareField = ({ record }) => (
  record && !record.isConditional ?
    <Labeled label="Price Per Share">
      <NumberField label="Price Per Share" record={record} source="pricePerShare" options={{ style: 'currency', currency: 'USD' }} />
    </Labeled> : null
);

const ConditionalOfferPricePerShareField = ({ record }) => (
  record && record.isConditional ?
    <Labeled label="Price Per Share">
      <NumberField label="Price Per Share" record={record} source="pricePerShare" options={{ style: 'currency', currency: 'USD' }} />
    </Labeled> : null
);

const ConditionalOfferMinSharePriceField = ({ record, ...rest }) => (
  record && record.isConditional ?
    <Labeled label="Min Share Price">
      <NumberField label="Min Share Price" record={record} source="minSharePrice" options={{ style: 'currency', currency: 'USD' }} {...rest} />
    </Labeled> : null
);

const ConditionalOfferMaxSharePriceField = ({ record, ...rest }) => (
  record && record.isConditional ?
    <Labeled label="Max Share Price">
      <NumberField label="Max Share Price" record={record} source="maxSharePrice" options={{ style: 'currency', currency: 'USD' }} {...rest} />
    </Labeled> : null
);

const ConditionalOfferLabel = ({ record }) => (
  record && record.isConditional ?
    <div>
      <br />
      <h3>Conditional Offer Details</h3>
    </div> : null
);

const FolioOffer = ({ record }) => (record && !record.isExternalOffer && record.folioId ?
  <Labeled label="Folio Private Placement Offer">
    <TextField label="Folio Private Placement Offer" record={record} source="folioId" />
  </Labeled> : null
);

const AccumulatedInvestments = ({ record }) => {
  const createdOfferOrders = record.orders ? record.orders.filter(order => order.status === 'created') : [];
  const createdOfferOrdersAccumulated = createdOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const submittedOfferOrders = record.orders ? record.orders.filter(order => order.status === 'submitted') : [];
  const submittedOfferOrdersAccumulated = submittedOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const completedOfferOrders = record.orders ? record.orders.filter(order => order.status === 'completed') : [];
  const completedOfferOrdersAccumulated = completedOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const rejectedOfferOrders = record.orders ? record.orders.filter(order => order.status === 'rejected') : [];
  const rejectedOfferOrdersAccumulated = rejectedOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const cancelledOfferOrders = record.orders ? record.orders.filter(order => order.status === 'cancelled') : [];
  const cancelledOfferOrdersAccumulated = cancelledOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const deletedOfferOrders = record.orders ? record.orders.filter(order => order.status === 'deleted') : [];
  const deletedOfferOrdersAccumulated = deletedOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  let summationOfFunnel = 0;
  if (record.offerOrderSummation.useCustom) {
    summationOfFunnel += record.offerOrderSummation.custom;
  } else {
    if (record.offerOrderSummation.created) {
      summationOfFunnel += createdOfferOrdersAccumulated;
    }
    if (record.offerOrderSummation.submitted) {
      summationOfFunnel += submittedOfferOrdersAccumulated;
    }
    if (record.offerOrderSummation.completed) {
      summationOfFunnel += completedOfferOrdersAccumulated;
    }
    if (record.offerOrderSummation.rejected) {
      summationOfFunnel += rejectedOfferOrdersAccumulated;
    }
    if (record.offerOrderSummation.cancelled) {
      summationOfFunnel += cancelledOfferOrdersAccumulated;
    }
    if (record.offerOrderSummation.deleted) {
      summationOfFunnel += deletedOfferOrdersAccumulated;
    }
  }

  if (record.status === 'active') {
    return null;
  }
  return (
    <div style={{ width: '100%' }}>
      <table style={{
        width: '100%',
        fontSize: 14,
        border: '1px solid grey',
        borderRadius: 15,
        padding: 10,
      }}
      >
        <tbody>
          <tr>
            <td colSpan="4" className="reactAdminLabel">Accumulated Investments</td>
          </tr>
          <tr style={{ paddingTop: 10 }}>
            <td style={{ width: '40%' }}>Offer Order Classification</td>
            <td style={{ width: '10%' }}>Count</td>
            <td>Sum of Investments</td>
            <td>Include in Summation</td>
          </tr>
          <tr>
            <td >Created Offer Orders (not submitted to Folio)</td>
            <td>{createdOfferOrders.length}</td>
            <td>{createdOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanField
                source="offerOrderSummation.created"
                record={record}
                label=""
              />
            </td>
          </tr>
          <tr>
            <td>Submitted Offer Orders (pending from Folio)</td>
            <td>{submittedOfferOrders.length}</td>
            <td>{submittedOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanField
                source="offerOrderSummation.submitted"
                record={record}
                label=""
              />
            </td>
          </tr>
          <tr>
            <td>Submitted Offer Orders (completed from Folio)</td>
            <td>{completedOfferOrders.length}</td>
            <td>{completedOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanField
                source="offerOrderSummation.completed"
                record={record}
                label=""
              />
            </td>
          </tr>
          <tr>
            <td>Submitted Offer Orders (rejected from Folio)</td>
            <td>{rejectedOfferOrders.length}</td>
            <td>{rejectedOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanField
                source="offerOrderSummation.rejected"
                record={record}
                label=""
              />
            </td>
          </tr>
          <tr>
            <td>Submitted Offer Orders (cancelled from Folio)</td>
            <td>{cancelledOfferOrders.length}</td>
            <td>{cancelledOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanField
                source="offerOrderSummation.cancelled"
                record={record}
                label=""
              />
            </td>
          </tr>
          <tr>
            <td>Cancelled Offer Orders (from Admin or User)</td>
            <td>{deletedOfferOrders.length}</td>
            <td>{deletedOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanField
                source="offerOrderSummation.deleted"
                record={record}
                label=""
              />
            </td>
          </tr>
          <tr>
            <td>
              Custom Accumulated Value
            </td>
            <td colSpan="2">
              <NumberField
                source="offerOrderSummation.custom"
                style={{ width: '100%' }}
                label="Custom Accumulated Value"
              />
            </td>
            <td>
              <BooleanField
                source="offerOrderSummation.useCustom"
                record={record}
                label=""
              />
            </td>
          </tr>
          <tr style={{ paddingTop: 10 }}>
            <td colSpan="2">
              Summation that will be displayed in Funnel:
            </td>
            <td colSpan="2">
              {summationOfFunnel.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const OrdersTab = ({ record }) => {
  const pendingOrdersAccumulatedValue = record.isExternalOffer && record.orders ? record.orders
    .filter(order => !['cancelled', 'approved', 'rejected'].includes(order.endStatus))
    .map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0) : 0;
  const approvedOrdersAccumulatedValue = record.isExternalOffer && record.orders ? record.orders
    .filter(order => order.endStatus === 'approved')
    .map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0) : 0;
  const rejectedOrdersAccumulatedValue = record.isExternalOffer && record.orders ? record.orders
    .filter(order => order.endStatus === 'rejected')
    .map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0) : 0;
  const cancelledOrdersAccumulatedValue = record.isExternalOffer && record.orders ? record.orders
    .filter(order => order.endStatus === 'cancelled')
    .map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0) : 0;

  const totalRaiseAmount = pendingOrdersAccumulatedValue + approvedOrdersAccumulatedValue;

  return (
    <table style={{ width: '100%' }}>
      <tbody>
        <tr>
          <td>
            <Labeled label="Total Raise Amount">
              {record.isExternalOffer ?
                <span>{totalRaiseAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span> :
                <span><NumberField record={record} source="totalRaisedAmount" options={{ style: 'currency', currency: 'USD' }} /></span>
              }
            </Labeled>
          </td>
          <td>
            <Labeled label="Maximum Raise Amount">
              <NumberField
                record={record}
                source="maxTotalRaiseCurrencyAmt"
                options={{ style: 'currency', currency: 'USD' }}
              />
            </Labeled>
          </td>
          <td>
            <Labeled label="Minimum Investment Currency">
              <NumberField
                record={record}
                source="minimumInvestCurrencyAmt"
                options={{ style: 'currency', currency: 'USD' }}
              />
            </Labeled>
          </td>
          <td>
            <Labeled label="Total Oversubscribed Amount">
              <FunctionField
                record={record}
                render={r => `${r.overSubscriptionValue > 0 ? `$${r.overSubscriptionValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}`}
              />
            </Labeled>
          </td>
        </tr>
        {record.isExternalOffer &&
          <tr>
            <td>
              <Labeled label="Total Raise Amount (Pending)">
                <span>{pendingOrdersAccumulatedValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
              </Labeled>
            </td>
            <td>
              <Labeled label="Total Raise Amount (Approved)">
                <span>{approvedOrdersAccumulatedValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
              </Labeled>
            </td>
            <td>
              <Labeled label="Total Raise Amount (Rejected)">
                <span>{rejectedOrdersAccumulatedValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
              </Labeled>
            </td>
            <td>
              <Labeled label="Total Raise Amount (Cancelled)">
                <span>{cancelledOrdersAccumulatedValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
              </Labeled>
            </td>
          </tr>
        }
      </tbody>
    </table>);
};

export default props => (
  <Show
    {...props}
    actions={<Actions path={props.location.pathname} />}
  >
    <TabbedShowLayout>
      <Tab label="Info">
        <TextField source="id" />
        <TextField source="shortUrlRoute" label="Short URL" />
        <ImageField source="logoUrl" label="Logo" />

        <TextField source="name" />

        <FunctionField
          label="Category"
          render={record => `${_.values(offeringCategory).find(item => item.value === record.category).label}`}
        />

        <FunctionField
          label="Offer source"
          render={record => `${_.values(externalOfferSelection).find(item => item.value === record.isExternalOffer).label}`}
        />

        <FunctionField
          label="Subscription Agreement"
          render={record => record.isExternalOffer && _.values(subscriptionAgreement).find(item => item.value === record.subscriptionAgreement).label}
        />

        <FolioOffer />
        <FunctionField
          addLabel={false}
          render={r => r.isExternalOffer && (
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td colSpan="2" style={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '0.8rem' }}>
                  <span>Wiring Instructions: (found below, only for Escrow Offers)</span>
                </td>
              </tr>
              <tr>
                <td style={{ width: '50%' }}>
                  <Labeled label="Bank Name">
                    <TextField source="wiringInstrBankName" record={r} />
                  </Labeled>
                </td>
                <td style={{ width: '50%' }}>
                  <Labeled label="ABA #">
                    <TextField source="wiringInstrABANumber" record={r} />
                  </Labeled>
                </td>
              </tr>
              <tr>
                <td style={{ width: '50%' }}>
                  <Labeled label="A/C #">
                    <TextField source="wiringInstrAccountNumber" record={r} />
                  </Labeled>
                </td>
                <td style={{ width: '50%' }}>
                  <Labeled label="A/C Name">
                    <TextField source="wiringInstrAccountName" record={r} />
                  </Labeled>
                </td>
              </tr>
              <tr>
                <td colSpan="2">
                  <Labeled label="Attention Line">
                    <TextField source="wiringInstrAttentionLine" record={r} />
                  </Labeled>
                </td>
              </tr>
              <tr>
                <td colSpan="2">
                  <hr style={{ borderTop: '1px solid #e0e0eb' }} />
                </td>
              </tr>
            </tbody>
          </table>
        )}
        />
        <FunctionField
          label="Security Delivery Information Options Type"
          render={record => record.isExternalOffer && !_.isEmpty(record.securityDeliveryInformationOptions)
            && !_.isEmpty(record.securityDeliveryInformationOptions.options) &&
            record.securityDeliveryInformationOptions.options.length > 0 &&
            _.values(securityDeliveryInformationOptionTypes).find(item => item.value === record.securityDeliveryInformationOptions.rootType).label}
        />
        <FunctionField
          label="Escrow Disclaimer"
          render={record => record.isExternalOffer && record.securityDeliveryInformationOptions && record.securityDeliveryInformationOptions.disclaimer}
        />
        <FunctionField
          label="Security Delivery Information Options"
          render={record => record.isExternalOffer &&
          <ArrayField source="securityDeliveryInformationOptions.options" record={record}>
            <Datagrid>
              <TextField source="optionLabel" label="Option Label" />
              <BooleanField source="isReadOnly" label="Is Read Only" />
              <BooleanField source="initialValue" label="Initial Value (Checked or Unchecked)" />
              <BooleanField source="hasTooltip" label="Has Tooltip" />
              <TextField source="optionTooltip" label="Tooltip text" />
            </Datagrid>
          </ArrayField>}
        />
        <ArrayField source="nonVisibleAppFields">
          <Datagrid>
            <TextField source="fieldName" />
          </Datagrid>
        </ArrayField>
        <TextField source="status" />
        <AccumulatedInvestments />
        <DateField source="startDate" />

        <BooleanField source="recurring" />

        <CloseDateOrDatesField label="Close Date(s)" />
        <ArrayField source="investmentRanges">
          <Datagrid>
            <TextField source="investmentRange" />
          </Datagrid>
        </ArrayField>
        <TextField source="anticipatedStartDate" />
        <BooleanField source="featured" />
        <BooleanField source="new" />
        <BooleanField source="visibleToEndUsers" />

        <TextField source="offerType" />
        <TextField source="industry" />
        <TextField source="sector" />
        <TextField source="securityType" />
        <TextField source="yield" />
        <TextField source="yieldDescription" />
        <TextField source="exitDescription" label="Exit Strategy/Liquidity" />
        <TextField component="pre" source="subTextOnClosed" label="Subtext On Closed Offer" multiline minRows="1" maxRows="4" />
      </Tab>

      <Tab label="Assets">
        <ArrayField source="assets">
          <Datagrid>
            <ImagePreview label="Preview" />
            <TextField source="type" />
            <UrlField source="url" label="Link" />
          </Datagrid>
        </ArrayField>
      </Tab>


      <Tab label="Price & Limits">
        <NumberField label="Minimum raise amount" source="minTotalRaiseCurrencyAmt" options={{ style: 'currency', currency: 'USD' }} />
        <NumberField label="Maximum Raise Amount" source="maxTotalRaiseCurrencyAmt" options={{ style: 'currency', currency: 'USD' }} />
        <NumberField label="Current Amount Raised" source="totalRaisedAmount" options={{ style: 'currency', currency: 'USD' }} />

        <StandardOfferPricePerShareField />
        <NumberField label="Minimum Investment Increment" source="minimumInvestIncCurrencyAmt" />

        <NumberField label="Minimum Investment Currency" source="minimumInvestCurrencyAmt" options={{ style: 'currency', currency: 'USD' }} />
        <NumberField label="Maximum Investment Currency" source="maximumInvestCurrencyAmt" options={{ style: 'currency', currency: 'USD' }} />

        <BooleanField source="allowRestrictedPersons" />

        <ConditionalOfferLabel />
        <ConditionalOfferPricePerShareField />
        <ConditionalOfferMinSharePriceField />
        <ConditionalOfferMaxSharePriceField />

      </Tab>

      <Tab label="Description">
        <StyledRichTextField source="description" />
      </Tab>

      <Tab label="Disclaimer">
        <StyledRichTextField source="disclaimer" />
      </Tab>

      <Tab label="Documents">
        <ReferenceManyField label="Offering Documents" reference="offeringDocuments" target="offerId">
          <Datagrid>
            <TextField source="docType" />
            <TextField source="url" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>
      <Tab label="Orders">
        <OrdersTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
);



// WEBPACK FOOTER //
// ./src/components/Offer/Show.js